import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Plus, Trash2 } from "lucide-react";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "", name: "", password: "", role: "viewer", team_ids: [],
  });
  const [err, setErr] = useState("");

  const load = async () => {
    const [u, t] = await Promise.all([api.get("/users"), api.get("/teams")]);
    setUsers(u.data);
    setTeams(t.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/users", form);
      setOpen(false);
      setForm({ email: "", name: "", password: "", role: "viewer", team_ids: [] });
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  return (
    <AppShell
      title="Users"
      actions={
        <button data-testid="new-user-button" className="ds-btn-primary flex items-center gap-2" onClick={() => setOpen(true)}>
          <Plus size={14} /> New user
        </button>
      }
    >
      <div className="ds-card overflow-x-auto">
        <table className="ds-table" data-testid="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Teams</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-semibold">{u.name}</td>
                <td className="font-mono text-[12px]">{u.email}</td>
                <td>{u.role}</td>
                <td className="text-[12px] text-[#4A4A52]">
                  {(u.team_ids || []).map((tid) => teams.find((t) => t.id === tid)?.name).filter(Boolean).join(", ") || "—"}
                </td>
                <td>{u.is_active ? "Active" : "Disabled"}</td>
                <td className="text-right">
                  <button
                    data-testid={`delete-user-${u.id}`}
                    className="ds-btn-ghost"
                    onClick={() => remove(u.id)}
                    aria-label="delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setOpen(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="ds-card p-6 w-full max-w-md"
            data-testid="new-user-form"
          >
            <h3 className="font-display text-lg font-bold mb-4">Create user</h3>
            <div className="space-y-3">
              <div>
                <label className="ds-label">Name</label>
                <input className="ds-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
              </div>
              <div>
                <label className="ds-label">Email</label>
                <input className="ds-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
              </div>
              <div>
                <label className="ds-label">Password</label>
                <input className="ds-input" required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
              </div>
              <div>
                <label className="ds-label">Role</label>
                <select className="ds-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
                  <option value="super_admin">Super Admin</option>
                  <option value="team_admin">Team Admin</option>
                  <option value="scanner">Scanner</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div>
                <label className="ds-label">Teams</label>
                <div className="border border-[#E5E5E9] p-2 max-h-32 overflow-y-auto">
                  {teams.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-[13px] py-0.5">
                      <input
                        type="checkbox"
                        checked={form.team_ids.includes(t.id)}
                        onChange={(e) => {
                          if (e.target.checked) setForm({ ...form, team_ids: [...form.team_ids, t.id] });
                          else setForm({ ...form, team_ids: form.team_ids.filter((x) => x !== t.id) });
                        }}
                      />
                      {t.name}
                    </label>
                  ))}
                </div>
              </div>
              {err && <div className="text-[12px] text-[#B42318]">{err}</div>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="ds-btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
              <button type="submit" className="ds-btn-primary" data-testid="new-user-submit">Create</button>
            </div>
          </form>
        </div>
      )}
    </AppShell>
  );
}
